//@ts-check
import {updateCatalog} from '@/services/ProductsService/ProductCatalog/productCatalog'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useDispatch} from 'react-redux'
import {transformCatalogToPublishPayload} from '../utils/productCatalogUtils'

const PRODUCT_CATALOG_QUERY_KEY = ['product-catalog-data']

/**
 * @typedef {Object} PublishProductCatalogVariables
 * @property {import('@/types/productCatalogTypes').ProductCategoryBrand[]} [brands]
 * @property {import('@/types/productCatalogTypes').ProductCategory[]} [categories]
 */

/**
 * @returns {{
 *   publishProductCatalog: (catalogData: {
 *     brands?: import('@/types/productCatalogTypes').ProductCategoryBrand[],
 *     categories?: import('@/types/productCatalogTypes').ProductCategory[],
 *   }) => Promise<unknown>,
 *   isPublishing: boolean,
 *   publishError: unknown,
 * }}
 */
export const usePublishProductCatalog = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  /**
   * @param {string | import('react').ReactNode} message
   * @param {'success' | 'error'} type
   */
  const showNotification = (message, type) => {
    dispatch(
      setToastNotifications({
        show: true,
        message,
        type,
      })
    )
  }

  const publishCatalogMutation = useMutation(
    /** @type {import('@tanstack/react-query').UseMutationOptions<unknown, Error, PublishProductCatalogVariables>} */ ({
      mutationFn: catalogData => {
        return updateCatalog(transformCatalogToPublishPayload(catalogData))
      },
      onSuccess: (_response, catalogData) => {
        // The read API may still return the old catalog immediately after saving.
        queryClient.setQueryData(PRODUCT_CATALOG_QUERY_KEY, catalogData)
        queryClient.invalidateQueries({queryKey: PRODUCT_CATALOG_QUERY_KEY})
        showNotification('Product catalog published successfully.', 'success')
      },
      onError: () => {
        showNotification('Error publishing product catalog. Please try again.', 'error')
      },
    })
  )

  return {
    publishProductCatalog: publishCatalogMutation.mutateAsync,
    isPublishing: publishCatalogMutation.isPending,
    publishError: publishCatalogMutation.error,
  }
}
